import type {
  IBuildIntegrationFetchEntriesProps,
  IGetIntegrationFetchSignatureProps,
  IIntegrationFetchEntry,
  IPruneProcessedAttachmentIdsProps,
  IReportIntegrationFetchProps,
} from "../types/uploads";
import { EFileUploadStatus } from "../types/uploads";
import { resolveIntegrationSourceName } from "./integration-source";

// NOTE: NSAI-5132 — the in-flight provider downloads, as the rows an Uploads
// page/screen renders. Shared so both clients describe the same download with
// the same fields; the platform half is only which attachment state they come
// from and who they are handed to.
export const buildIntegrationFetchEntries = ({
  attachments,
}: IBuildIntegrationFetchEntriesProps): IIntegrationFetchEntry[] =>
  attachments.map((attachment) => ({
    id: attachment.id,
    fileName: attachment.name,
    progress: attachment.progress ?? 0,
    downloadedBytes: attachment.downloadedBytes,
    totalBytes: attachment.totalBytes,
    sourceName: resolveIntegrationSourceName({
      provider: attachment.provider,
    }),
    status: EFileUploadStatus.FETCHING,
    cancel: attachment.cancel,
  }));

// NOTE: NSAI-5132 — a value signature of the reported list. Every download
// progress tick replaces the whole attachment array, so comparing identities
// would push a new list (and a caller re-render) even when nothing a row
// displays has moved.
export const getIntegrationFetchSignature = ({
  entries,
}: IGetIntegrationFetchSignatureProps): string =>
  entries
    .map(
      (entry) =>
        `${entry.id}:${entry.progress}:${entry.downloadedBytes ?? 0}:${entry.totalBytes ?? 0}`,
    )
    .join("|");

// NOTE: NSAI-5132 — the whole "report the in-flight downloads, but only when
// something a row shows has moved" rule, kept in one place because both clients
// drive it from an effect that re-runs on every progress tick. The last
// signature lives in the closure rather than in a per-platform ref, so the two
// hooks are left with nothing to keep in step but the call itself.
export const createIntegrationFetchReporter = (): ((
  props: IReportIntegrationFetchProps,
) => void) => {
  let lastSignature: string | null = null;

  return ({ attachments, onChange }: IReportIntegrationFetchProps): void => {
    const entries = buildIntegrationFetchEntries({ attachments });
    const signature = getIntegrationFetchSignature({ entries });

    if (signature === lastSignature) {
      return;
    }

    lastSignature = signature;
    onChange(entries);
  };
};

// NOTE: Drops IDs that are no longer in state from the seen-set so it does not
// grow unbounded across a session. Mutates in place: the caller holds the set
// in a ref, and replacing it would lose entries added by a concurrent pass.
export const pruneProcessedAttachmentIds = ({
  processedIds,
  currentIds,
}: IPruneProcessedAttachmentIdsProps): void => {
  for (const seenId of processedIds) {
    if (!currentIds.has(seenId)) {
      processedIds.delete(seenId);
    }
  }
};
